import React, { useState, useEffect } from 'react';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';
import { Box, Container, Typography, Button, CircularProgress, Grid } from '@mui/material';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const STORAGE_KEY = 'acumuladoMachine8_grafica';

const theme = {
  bg:          '#04091a',
  cardBg:      '#071525',
  cardBg2:     '#050e1e',
  text:        '#B8F1FF',
  textMuted:   '#3a5a80',
  textSub:     '#8ab0cc',
  borderCard:  'rgba(40,120,255,0.28)',
  shadow:      '0 8px 32px rgba(0,0,0,0.85), 0 2px 8px rgba(0,0,0,0.6), inset 0 1px 0 rgba(60,140,255,0.18)',
  shadowInset: 'inset 0 3px 12px rgba(0,0,0,0.75), inset 0 1px 4px rgba(0,0,0,0.6)',
};

const dateInputSx = {
  backgroundColor: theme.cardBg2,
  color: theme.text,
  border: `1px solid ${theme.borderCard}`,
  borderRadius: '6px',
  px: 1.5,
  py: 1.2,
  fontSize: '1rem',
  fontFamily: 'Roboto, sans-serif',
  outline: 'none',
  width: '100%',
  cursor: 'pointer',
  colorScheme: 'dark',
  '&:focus': { borderColor: '#4a90d9' },
  '&::-webkit-calendar-picker-indicator': { filter: 'invert(0.7) sepia(1) saturate(3) hue-rotate(180deg)', cursor: 'pointer' },
};

const AcumuladoMachine8Grafica = () => {
  const [fechaInicio, setFechaInicio] = useState('');
  const [fechaFin, setFechaFin]       = useState('');
  const [loading, setLoading]         = useState(false);
  const [chartData, setChartData]     = useState(null);
  const [hayDatos, setHayDatos]       = useState(false);
  const [totalRegistros, setTotalRegistros] = useState(0);
  const [error, setError]             = useState('');

  useEffect(() => {
    const guardado = localStorage.getItem(STORAGE_KEY);
    if (guardado) {
      try {
        const parsed = JSON.parse(guardado);
        if (parsed.chartData?.datasets?.[0]?.data?.some(v => v < 0)) {
          localStorage.removeItem(STORAGE_KEY); return;
        }
        setFechaInicio(parsed.fechaInicio || '');
        setFechaFin(parsed.fechaFin || '');
        setChartData(parsed.chartData);
        setHayDatos(parsed.hayDatos);
        setTotalRegistros(parsed.totalRegistros);
      } catch { localStorage.removeItem(STORAGE_KEY); }
    }
  }, []);

  const fetchData = async () => {
    if (!fechaInicio || !fechaFin) { setError('Selecciona ambas fechas'); return; }
    setLoading(true); setError('');
    try {
      const API_URL = process.env.REACT_APP_API_URL || '';
      const BASE_URL = API_URL.endsWith('/api') ? API_URL.replace(/\/api$/, '') : API_URL;
      const res = await fetch(`${BASE_URL}/api/machine8/acumulado-diario?fechaInicio=${fechaInicio}&fechaFin=${fechaFin}`);
      if (!res.ok) throw new Error(`Error ${res.status}`);
      const data = await res.json();

      const fmt = s => {
        if (s.includes('/')) { const [m, d, y] = s.split('/'); return `${d.padStart(2,'0')}/${m.padStart(2,'0')}/${y}`; }
        return s;
      };

      if (data.length === 0) {
        setHayDatos(false); setTotalRegistros(0); setChartData(null);
        localStorage.setItem(STORAGE_KEY, JSON.stringify({ fechaInicio, fechaFin, chartData: null, hayDatos: false, totalRegistros: 0 }));
      } else {
        setHayDatos(true); setTotalRegistros(data.length);
        const cd = {
          labels: data.map(d => fmt(d.fecha)),
          datasets: [{ label: 'Acumulado Mts/lineal por Día', data: data.map(d => d.acumuladoRPM), backgroundColor: 'rgba(0,203,160,0.7)', borderColor: '#00cba0', borderWidth: 1, borderRadius: 4 }]
        };
        setChartData(cd);
        localStorage.setItem(STORAGE_KEY, JSON.stringify({ fechaInicio, fechaFin, chartData: cd, hayDatos: true, totalRegistros: data.length }));
      }
    } catch (e) {
      setError('Error al cargar datos: ' + e.message);
    } finally { setLoading(false); }
  };

  const options = {
    responsive: true, maintainAspectRatio: false,
    plugins: {
      legend: { position: 'top', labels: { color: '#8ab0cc', font: { size: 11 } } },
      title: { display: true, text: 'Acumulado MTS/lineal Diario — Machine8', color: '#8ab0cc', font: { family: 'Roboto', size: 13 } },
      tooltip: { callbacks: { label: ctx => ` ${ctx.parsed.y.toLocaleString('es-CO')} Mts/Lineales` } }
    },
    scales: {
      x: { grid: { color: 'rgba(30,90,180,0.2)', lineWidth: 0.5 }, ticks: { color: '#8ab0cc', font: { size: 10 }, maxRotation: 45 }, title: { display: true, text: 'Fecha', color: '#8ab0cc', font: { size: 10 } } },
      y: { beginAtZero: true, grid: { color: 'rgba(30,90,180,0.2)', lineWidth: 0.5 }, ticks: { color: '#8ab0cc', font: { size: 10 }, callback: v => v.toLocaleString('es-CO') }, title: { display: true, text: 'Mts/Lineales', color: '#8ab0cc', font: { size: 10 } } }
    }
  };

  return (
    <Box sx={{ backgroundColor: theme.bg, minHeight: '100vh', py: 3, px: { xs: 1, sm: 2 } }}>
      <Container maxWidth="xl">
        <Typography variant="h5" sx={{ color: theme.text, mb: 3, fontWeight: 600, letterSpacing: 1 }}>
          Acumulado Diario Machine 8
        </Typography>

        <Box sx={{ backgroundColor: theme.cardBg, border: `1px solid ${theme.borderCard}`, borderRadius: '10px', boxShadow: theme.shadow, p: { xs: 2, sm: 3 }, mb: 3 }}>
          <Grid container spacing={2} alignItems="flex-end">
            <Grid item xs={12} sm={4}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Typography sx={{ color: theme.textSub, fontSize: 12, pl: 0.5 }}>Fecha Inicio</Typography>
                <Box component="input" type="date" value={fechaInicio} onChange={e => setFechaInicio(e.target.value)} sx={dateInputSx} />
              </Box>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Typography sx={{ color: theme.textSub, fontSize: 12, pl: 0.5 }}>Fecha Fin</Typography>
                <Box component="input" type="date" value={fechaFin} onChange={e => setFechaFin(e.target.value)} sx={dateInputSx} />
              </Box>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Button variant="contained" fullWidth onClick={fetchData} disabled={loading}
                sx={{ backgroundColor: '#0d4f8c', color: '#B8F1FF', fontWeight: 600, height: 50, '&:hover': { backgroundColor: '#1565c0' }, '&.Mui-disabled': { backgroundColor: theme.cardBg2, color: theme.textMuted } }}>
                {loading ? <CircularProgress size={22} color="inherit" /> : 'Consultar'}
              </Button>
            </Grid>
          </Grid>
          {error && <Typography sx={{ color: '#ef5350', mt: 1.5, fontSize: 13 }}>{error}</Typography>}
        </Box>

        {hayDatos && chartData && (
          <Box sx={{ backgroundColor: theme.cardBg, border: `1px solid ${theme.borderCard}`, borderRadius: '10px', boxShadow: theme.shadow, p: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
              <Typography sx={{ color: '#00cba0', fontSize: 13, fontWeight: 600 }}>
                {totalRegistros} {totalRegistros === 1 ? 'día' : 'días'} registrados
              </Typography>
            </Box>
            <Box sx={{ height: { xs: 320, sm: 460 }, backgroundColor: theme.cardBg2, borderRadius: '8px', border: `1px solid ${theme.borderCard}`, boxShadow: theme.shadowInset, p: 2 }}>
              <Bar data={chartData} options={options} />
            </Box>
          </Box>
        )}

        {!hayDatos && !loading && (
          <Box sx={{ textAlign: 'center', py: 10 }}>
            <Typography sx={{ color: theme.textMuted, fontSize: 15 }}>
              Selecciona un rango de fechas y pulsa Consultar
            </Typography>
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default AcumuladoMachine8Grafica;
